/**
 * Inventory Movement Tracker (Jun 2026, P0).
 *
 * Read-only report over the existing event log. Surfaces starting /
 * ending balances + net change per (item_id, branch) for a chosen date
 * window. Top movers float to the top; clicking "Events" on any row
 * opens a chronological drill-down of the exact mutations that drove
 * the change.
 *
 * Backed by:
 *   GET /api/inventory/movement-report
 *   GET /api/inventory/movement-report/events
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronRight, TrendingUp, TrendingDown, Search,
  Calendar, X, Activity, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

// Default window: last 30 days inclusive. ISO YYYY-MM-DD only.
const fmtDate = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const fmtQty = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return sign + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtAbs = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Render any ISO/datetime string as IST (Asia/Kolkata). Stored timestamps
// are UTC (server uses `datetime.now(timezone.utc).isoformat()`); displaying
// the raw string previously showed UTC, off by 5h30m from what factory
// teams see on the floor. Format: `YYYY-MM-DD HH:mm:ss` (24h, no tz suffix
// — the column header carries the "IST" label).
const _IST_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
});
const fmtIST = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 19).replace('T', ' ');
  // `en-CA` gives `YYYY-MM-DD, HH:mm:ss`; strip the comma for parity with
  // the old "YYYY-MM-DD HH:mm:ss" layout the table column was sized for.
  return _IST_FMT.format(d).replace(', ', ' ');
};

// Net-change colour: green for net increase, red for net decrease, slate for zero.
const netClass = (n) => {
  if (!n) return 'text-slate-500';
  return n > 0 ? 'text-emerald-600' : 'text-rose-600';
};

const netBgClass = (n) => {
  if (!n) return 'bg-slate-50 border-slate-200';
  return n > 0
    ? 'bg-emerald-50 border-emerald-200'
    : 'bg-rose-50 border-rose-200';
};

export default function InventoryMovement() {
  const [startDate, setStartDate] = useState(fmtDate(daysAgo(30)));
  const [endDate, setEndDate] = useState(fmtDate(today()));
  const [itemKind, setItemKind] = useState('BOTH');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [expandedBranches, setExpandedBranches] = useState({});

  // Drill-down modal state
  const [drillItem, setDrillItem] = useState(null);  // { item_id, branch, item_name }
  const [drillEvents, setDrillEvents] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error('Pick a date range');
      return;
    }
    setLoading(true);
    try {
      const params = {
        item_kind: itemKind,
        start_date: startDate,
        end_date: endDate,
      };
      if (branchFilter && branchFilter !== 'ALL') params.branch = branchFilter;
      const { data } = await axios.get(`${API}/api/inventory/movement-report`, { params });
      setReport(data);
      // Auto-expand the top mover so the user lands on actionable data.
      if (data?.branch_summary?.length) {
        setExpandedBranches({ [data.branch_summary[0].branch]: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Failed to load movement report: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, itemKind, branchFilter]);

  // Initial load + auto-refresh on filter change.
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const openDrill = useCallback(async (branch, item) => {
    setDrillItem({ ...item, branch });
    setDrillEvents([]);
    setDrillLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/inventory/movement-report/events`, {
        params: {
          item_kind: item.item_kind || itemKind,
          item_id: item.item_id,
          branch,
          start_date: startDate,
          end_date: endDate,
        },
      });
      setDrillEvents(data?.events || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Failed to load events: ${msg}`);
    } finally {
      setDrillLoading(false);
    }
  }, [itemKind, startDate, endDate]);

  const closeDrill = () => { setDrillItem(null); setDrillEvents([]); };

  const toggleBranch = (br) => setExpandedBranches((m) => ({ ...m, [br]: !m[br] }));

  // Client-side branch dropdown built from the report's actual data so we
  // never offer a branch with no events in the window.
  const branchOptions = useMemo(() => {
    if (!report?.branch_summary) return [];
    return [...new Set(report.branch_summary.map((b) => b.branch))].filter(Boolean).sort();
  }, [report]);

  // Client-side item search. Applied INSIDE each branch's item list so
  // matching items still surface under their correct branch grouping.
  const filteredBranchSummary = useMemo(() => {
    if (!report?.branch_summary) return [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return report.branch_summary;
    return report.branch_summary
      .map((b) => ({
        ...b,
        items: (b.items || []).filter((it) => {
          const id = (it.item_id || '').toLowerCase();
          const nm = (it.item_name || '').toLowerCase();
          return id.includes(term) || nm.includes(term);
        }),
      }))
      .filter((b) => b.items.length > 0);
  }, [report, searchTerm]);

  return (
    <div className="space-y-6 p-6" data-testid="inventory-movement-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title">
            Inventory Movement
          </h1>
          <p className="text-sm text-slate-500">
            Starting → ending balance per item, sourced from the event log. Biggest movers first.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" onClick={fetchReport} disabled={loading}
            data-testid="refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Activity className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 bg-white border rounded-lg">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Start date
          </label>
          <Input
            type="date" value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="start-date-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> End date
          </label>
          <Input
            type="date" value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="end-date-input"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1">Item kind</label>
          <Select value={itemKind} onValueChange={setItemKind}>
            <SelectTrigger data-testid="item-kind-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BOTH">Both (FG + RM)</SelectItem>
              <SelectItem value="FG">Finished Goods</SelectItem>
              <SelectItem value="RM">Raw Materials</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1">Branch</label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger data-testid="branch-filter-select">
              <SelectValue placeholder="All branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All branches</SelectItem>
              {branchOptions.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1">Search item</label>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-3 text-slate-400" />
            <Input
              className="pl-7" placeholder="item_id or name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Top Movers */}
      {report?.top_movers?.by_item?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopMoversCard
            title="Top Branches"
            rows={report.top_movers.by_branch}
            labelKey="branch"
          />
          <TopMoversCard
            title="Top Items"
            rows={report.top_movers.by_item}
            labelKey="item_id"
            subKey="item_name"
            extraKey="branch"
          />
        </div>
      )}

      {/* Branch cards */}
      {loading && !report && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading movement report…
        </div>
      )}
      {!loading && (!report?.branch_summary?.length) && (
        <div className="text-center py-20 text-slate-500" data-testid="empty-state">
          No inventory movement in this window.
        </div>
      )}
      <div className="space-y-3">
        {filteredBranchSummary.map((b) => (
          <BranchCard
            key={b.branch || '(no-branch)'}
            branch={b}
            expanded={!!expandedBranches[b.branch]}
            onToggle={() => toggleBranch(b.branch)}
            onDrill={(item) => openDrill(b.branch, item)}
          />
        ))}
      </div>

      {/* Drill-down modal */}
      <Dialog open={!!drillItem} onOpenChange={(o) => { if (!o) closeDrill(); }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="drill-modal">
          <DialogHeader>
            <DialogTitle>
              Events: <span className="font-mono text-base">{drillItem?.item_id}</span>
              <span className="text-sm text-slate-500 font-normal ml-2">
                {drillItem?.item_name && `· ${drillItem.item_name} `}@ {drillItem?.branch}
              </span>
            </DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : drillEvents.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No events in this window.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-600 border-b">
                <tr>
                  <th className="text-left py-2">Timestamp (IST)</th>
                  <th className="text-left py-2">Event type</th>
                  <th className="text-right py-2">Change</th>
                  <th className="text-right py-2">Running</th>
                  <th className="text-left py-2 pl-3">Reference</th>
                </tr>
              </thead>
              <tbody>
                {drillEvents.map((e) => (
                  <tr key={e.event_id} className="border-b last:border-0" data-testid="drill-event-row">
                    <td className="py-2 font-mono text-xs" title={e.timestamp || ''}>{fmtIST(e.timestamp)}</td>
                    <td className="py-2">
                      <Badge variant="outline" className="text-xs">{e.business_event_type}</Badge>
                    </td>
                    <td className={`py-2 text-right font-mono ${netClass(e.change)}`}>{fmtQty(e.change)}</td>
                    <td className="py-2 text-right font-mono">{fmtAbs(e.running_balance)}</td>
                    <td className="py-2 pl-3 font-mono text-xs text-slate-500">{e.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function TopMoversCard({ title, rows, labelKey, subKey, extraKey }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" />{title}
      </h3>
      <div className="space-y-1.5">
        {rows.slice(0, 5).map((r, i) => (
          <div key={`${r[labelKey]}-${i}`} className="flex items-center justify-between text-sm">
            <div className="min-w-0 flex-1 truncate">
              <span className="font-mono text-xs">{r[labelKey] || '(blank)'}</span>
              {subKey && r[subKey] && (
                <span className="text-slate-500 ml-2 text-xs">· {r[subKey]}</span>
              )}
              {extraKey && r[extraKey] && (
                <span className="text-slate-400 ml-2 text-xs">@ {r[extraKey]}</span>
              )}
            </div>
            <span className={`font-mono font-semibold ${netClass(r.net_change)}`}>
              {fmtQty(r.net_change)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BranchCard({ branch, expanded, onToggle, onDrill }) {
  const net = branch.net_change;
  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${netBgClass(net)}`}
         data-testid="branch-card">
      <button
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-black/5"
        onClick={onToggle}
        data-testid={`toggle-branch-${branch.branch}`}
      >
        <div className="flex items-center gap-3">
          {expanded
            ? <ChevronDown className="w-4 h-4 text-slate-500" />
            : <ChevronRight className="w-4 h-4 text-slate-500" />}
          <div className="text-left">
            <div className="font-semibold">{branch.branch || '(no branch)'}</div>
            <div className="text-xs text-slate-500">{branch.items_moved} items moved</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs text-slate-500">Starting</div>
            <div className="font-mono">{fmtAbs(branch.starting_total)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Ending</div>
            <div className="font-mono">{fmtAbs(branch.ending_total)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 flex items-center justify-end gap-1">
              {net > 0 ? <TrendingUp className="w-3 h-3" /> : net < 0 ? <TrendingDown className="w-3 h-3" /> : null}
              Net
            </div>
            <div className={`font-mono font-bold ${netClass(net)}`}>{fmtQty(net)}</div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="bg-white border-t">
          <table className="w-full text-sm">
            <thead className="text-slate-600 border-b bg-slate-50">
              <tr>
                <th className="text-left py-2 px-4">Item ID</th>
                <th className="text-left py-2">Name</th>
                <th className="text-right py-2">Start</th>
                <th className="text-right py-2">End</th>
                <th className="text-right py-2 pr-3">Net</th>
                <th className="text-left py-2 pl-3">Breakdown</th>
                <th className="text-right py-2 pr-4">Events</th>
              </tr>
            </thead>
            <tbody>
              {branch.items.map((it) => (
                <tr key={it.item_id} className="border-b last:border-0 hover:bg-slate-50" data-testid="item-row">
                  <td className="py-2 px-4 font-mono text-xs">
                    {it.item_id}
                    {it.item_kind && (
                      <Badge variant="outline" className="ml-2 text-[10px]">{it.item_kind}</Badge>
                    )}
                  </td>
                  <td className="py-2 text-xs text-slate-600 max-w-[260px] truncate">{it.item_name || '—'}</td>
                  <td className="py-2 text-right font-mono">{fmtAbs(it.starting)}</td>
                  <td className="py-2 text-right font-mono">{fmtAbs(it.ending)}</td>
                  <td className={`py-2 text-right pr-3 font-mono font-semibold ${netClass(it.net_change)}`}>
                    {fmtQty(it.net_change)}
                  </td>
                  <td className="py-2 pl-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(it.breakdown || {}).map(([bet, qty]) => (
                        <span
                          key={bet}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                            qty > 0 ? 'bg-emerald-100 text-emerald-700'
                            : qty < 0 ? 'bg-rose-100 text-rose-700'
                            : 'bg-slate-100 text-slate-600'
                          }`}
                          title={bet}
                        >
                          {bet}: {fmtQty(qty)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Button
                      variant="outline" size="sm" onClick={() => onDrill(it)}
                      data-testid="open-drill-btn"
                    >
                      {it.event_count}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
